// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { Permission } from '../constants/permissions.js';
/**
 * Collection names, declared beside the types they hold.
 *
 * The 62.7.0 convention, and 62.8.0's gate found why it matters: `indexes.ts`
 * spelled thirteen collection names as literals while the migration imported the
 * constants, so a divergence would surface as a collection scan rather than an
 * error. These are imported everywhere — indexes, migration, services, routes.
 */
export const LOGISTICS_SHIPMENT_COLLECTION = 'grc_logistics_shipments';
export const LOGISTICS_CARGO_ITEM_COLLECTION = 'grc_logistics_cargo_items';
export const LOGISTICS_CUSTOMS_RECORD_COLLECTION = 'grc_logistics_customs_records';
export const LOGISTICS_CLEARANCE_EVENT_COLLECTION = 'grc_logistics_clearance_events';
export const LOGISTICS_CORRESPONDENCE_COLLECTION = 'grc_logistics_authority_correspondence';
export const LOGISTICS_PARCEL_COLLECTION = 'grc_logistics_parcels';
export const LOGISTICS_PARCEL_EVENT_COLLECTION = 'grc_logistics_parcel_events';
export const LOGISTICS_DELIVERY_PROOF_COLLECTION = 'grc_logistics_delivery_proofs';
export const LOGISTICS_CLAIM_COLLECTION = 'grc_logistics_claims';
export const LOGISTICS_AUTHORISATION_COLLECTION = 'grc_logistics_authorisations';
/**
 * Every collection this module owns.
 *
 * Derived membership rather than a transcribed list: the evidence-pack
 * collector, the export service and the migration each assert their own subject
 * against this, so a register that nothing collects, exports or migrates fails
 * by name instead of being quietly absent — 62.8.0's collector finding, where a
 * pack assembled by a tenant with data was byte-identical to one for a tenant
 * with none.
 */
export const LOGISTICS_COLLECTIONS = [
    LOGISTICS_SHIPMENT_COLLECTION,
    LOGISTICS_CARGO_ITEM_COLLECTION,
    LOGISTICS_CUSTOMS_RECORD_COLLECTION,
    LOGISTICS_CLEARANCE_EVENT_COLLECTION,
    LOGISTICS_CORRESPONDENCE_COLLECTION,
    LOGISTICS_PARCEL_COLLECTION,
    LOGISTICS_PARCEL_EVENT_COLLECTION,
    LOGISTICS_DELIVERY_PROOF_COLLECTION,
    LOGISTICS_CLAIM_COLLECTION,
    LOGISTICS_AUTHORISATION_COLLECTION,
];
// ── Shipment identity ────────────────────────────────────────────
/**
 * How a shipment is referred to by whoever is referring to it.
 *
 * **This union is the reason the shipment aggregate exists.** The customs
 * persona's third pain point, verbatim: *"the same consignment is referenced by
 * booking number, house bill, master bill and declaration number, with no record
 * tying them together."* An aggregate that carried one `reference` field would
 * reproduce that defect with a database behind it.
 *
 * **Why a platform union and not a pack vocabulary.** A house bill of lading and
 * a master bill of lading are commercial transport documents defined by carrier
 * and forwarder practice worldwide; a booking reference is the carrier's own. No
 * state enacted them and no authority supervises them. The one member that *is*
 * issued by an authority — `declaration_reference` — deliberately names the
 * platform concept ("the reference the authority gave this filing") and not any
 * jurisdiction's word for it, and the artefact's own kind comes from the pack.
 */
export const SHIPMENT_IDENTIFIER_SCHEMES = [
    'booking_reference',
    'house_transport_document',
    'master_transport_document',
    'declaration_reference',
    'carrier_tracking_reference',
    'internal_reference',
];
// ── Parties ──────────────────────────────────────────────────────
/**
 * A role a party plays in a shipment.
 *
 * **Why a platform union.** Consignor, consignee, carrier and notify party are
 * the standing vocabulary of international transport documents; a customs broker
 * is the intermediary role every customs regime has some form of. What differs
 * per jurisdiction is *which roles may lodge a declaration and what each owes* —
 * and that is the pack's `logistics_service_role` vocabulary, which describes
 * the **organisation's own** regulated role, not a counterparty's position on
 * one shipment. The two are kept apart deliberately: they answer different
 * questions and a single list would answer neither.
 *
 * Sprint 62.25.0 AC8 — the organisation's own role is recorded on
 * `CustomsRecord.service_role_code`; a party's role on the shipment stays here
 * (and on `CustomsRecord.lodged_by_role`).
 *
 * Sprint 62.55.0, gap G20 (owner, 2026-10-26) — `digital_platform`: an
 * e-commerce or digital platform that is a counterparty to the shipment's
 * declaration, named here when a person has determined it plays a part. The
 * role says only that a person named the platform as a party; what an
 * installed regime asks of such a platform is the pack's, and nothing here
 * derives it.
 */
export const SHIPMENT_PARTY_ROLES = [
    'consignor',
    'consignee',
    'carrier',
    'customs_broker',
    'notify_party',
    'digital_platform',
];
/**
 * Which canonical register a party resolves in.
 *
 * There is **no unified party type in this codebase** — measured at kickoff.
 * Three stores each hold part of the answer, and the right one depends on what
 * the party is rather than on what is convenient:
 *
 * - `vendor` → `grc_vendors`. A carrier or a broker is an organisation the
 *   tenant contracts with, and its due-diligence position already lives there.
 * - `data_subject` → `grc_data_subject_profiles`. A consignor or consignee who
 *   is a natural person is a data subject with rights, and the whole point of
 *   VN-LOG-R05 is that they are treated as one.
 * - `identity_profile` → `grc_identity_profiles`. A party whose identity the
 *   organisation must be able to *prove* it checked (62.5.0).
 *
 * Note the collection is `grc_data_subject_profiles`. `grc_data_subjects` does
 * not exist, and the only occurrence of that string in the tree is a comment
 * warning that it does not — somebody has been caught by this already.
 */
export const SHIPMENT_PARTY_KINDS = ['vendor', 'data_subject', 'identity_profile'];
// ── Shipment ─────────────────────────────────────────────────────
/**
 * The direction of a movement relative to the organisation's own customs
 * territory.
 *
 * Not a declaration subtype. The packs name no import/export/transit
 * declaration taxonomy — swept for and absent — so this records the plain
 * geography of the movement and nothing about which procedure applies.
 */
export const SHIPMENT_DIRECTIONS = ['inbound', 'outbound', 'transit', 'domestic'];
/**
 * How the goods move.
 *
 * A physical fact about the world, not a legal category. The packs name no
 * transport mode at all — zero hits for air, sea, maritime, road, rail — so this
 * is the platform's own, and it exists because a reader reconstructing a
 * shipment two years later needs it.
 */
export const SHIPMENT_TRANSPORT_MODES = ['air', 'sea', 'road', 'rail', 'multimodal'];
export const SHIPMENT_STATUSES = ['open', 'closed', 'cancelled'];
// ── Customs evidence ─────────────────────────────────────────────
export const CUSTOMS_RECORD_STATUSES = [
    'draft',
    'lodged',
    'amended',
    'closed',
    'withdrawn',
];
// ── Clearance ────────────────────────────────────────────────────
/**
 * What the authority did.
 *
 * **Every one of these five is attested verbatim in the logistics pack**, twice
 * and consistently: `procedures.json` VN-LOG-P2 step 3 — *"Record inspection,
 * hold, release or corrective action"* — and requirement VN-LOG-R07 — *"holds,
 * release and closure"*, whose Vietnamese reads *"tạm giữ, thông quan và kết
 * thúc"*.
 *
 * **Why a platform union even so.** These are the universal states of customs
 * control: goods are examined, stopped, let go, or the matter ends. What is
 * jurisdiction-specific is the *risk-channel classification* that decides which
 * shipments get examined — and the packs name none of it. That classification is
 * real; it is prescribed by Circular 38/2015/TT-BTC as amended. It is
 * **deliberately not here and not in the packs**, because the logistics pack's
 * own recorded posture withholds unverified customs artefacts, and overriding a
 * content decision like that is a Compliance Manager's call. Recorded as a
 * decision with a named owner, not as an omission a later sprint fills in
 * silently.
 */
export const CLEARANCE_OUTCOMES = [
    'inspection',
    'hold',
    'release',
    'corrective_action',
    'closure',
];
// ── Parcel ───────────────────────────────────────────────────────
export const PARCEL_STATUSES = ['active', 'closed'];
/**
 * What happened to a parcel.
 *
 * **All eleven are attested verbatim** in the postal pack, twice: VN-POST-R06 —
 * *"custody events, attempts, proof, exceptions, return, loss, damage,
 * complaint, remedy and closure"* — and `procedures.json` VN-POST-P2 step 2.
 *
 * **Why a platform union.** A parcel is attempted, delivered, refused, lost or
 * damaged in every country that has parcels; these are operational facts, not
 * legal categories. What *is* jurisdiction-specific — the reason a delivery
 * failed, the kinds a claim may be brought in, what counts as proof — the packs
 * name **none of**, swept for and absent in both locales. So there is no failure
 * reason enum, no claim kind enum and no proof kind enum anywhere below: each is
 * free text carrying what the operator recorded, and the absence is stated here
 * rather than filled in with a plausible list.
 */
export const PARCEL_EVENT_KINDS = [
    'custody',
    'attempt',
    'proof',
    'exception',
    'failure',
    'return',
    'loss',
    'damage',
    'complaint',
    'remedy',
    'closure',
];
// ── Claims ───────────────────────────────────────────────────────
export const CLAIM_STATUSES = ['open', 'investigating', 'resolved', 'rejected'];
// ── Postal authorisation ─────────────────────────────────────────
export const AUTHORISATION_STATUSES = [
    'draft',
    'submitted',
    'in_force',
    'superseded',
    'withdrawn',
];
// ── Attachment owner types ───────────────────────────────────────
/**
 * The owner types this module registers.
 *
 * Three structures must agree — `AttachmentOwnerType` in `attachment.ts`,
 * `OWNER_TYPE_ALLOWLIST` + `OWNER_TYPE_COLLECTIONS` in the attachments module,
 * and `OWNER_TYPE_DOWNLOAD_POLICY` (64.0.0) — and only two of the three are
 * guarded by a drift test. Declared here so all three import one list.
 *
 * `logistics_delivery_proof` is separated from the other evidence types on
 * purpose: it is the one that holds a signature image or a photograph of a
 * person's doorstep, and 64.0.0's download policy gates retrieval on the
 * permission that gated creation.
 *
 * Gate E12.5 pass 2: this list had **no consumer anywhere in the tree** — the
 * sentence above claiming all three structures import it was false — and it was
 * already one short, missing `logistics_delivery_proof_export`. A declared list
 * nothing reads and nothing compares is not a contract, so
 * `allowlist-drift.test.ts` now asserts it equals every `logistics_`-prefixed
 * `AttachmentOwnerType` value, both directions.
 */
export const LOGISTICS_ATTACHMENT_OWNER_TYPES = [
    'logistics_shipment_document',
    'logistics_customs_evidence',
    'logistics_clearance_evidence',
    'logistics_delivery_proof',
    'logistics_parcel_event_evidence',
    'logistics_claim_evidence',
    'logistics_authorisation_evidence',
    'logistics_export',
    'logistics_customs_export',
    'logistics_parcel_export',
    'logistics_claim_export',
    'logistics_authorisation_export',
    'logistics_delivery_proof_export',
];
// ── R-33 export contract ─────────────────────────────────────────
/**
 * What this module can export, declared in **shared** and not in the backend
 * service that produces it.
 *
 * 62.8.0 shipped its equivalent union inside the backend and its gate found the
 * consequence at pass 2: eight correct, permission-gated, tested export routes
 * with `ExportFormatMenu` on **0 of 9 pages** and no client method, because the
 * union was unreachable from the frontend. AC8's own words are about what an
 * inspector can be handed, and eight routes nobody can call hand over nothing.
 *
 * So the contract lives here, where both the backend route table and the
 * frontend export control read it, and neither restates it.
 */
export const LOGISTICS_EXPORT_KINDS = [
    'shipments',
    'cargo_items',
    'customs_records',
    'clearance_events',
    'authority_correspondence',
    'parcels',
    'parcel_events',
    'delivery_proofs',
    'claims',
    'authorisations',
];
/**
 * The owner types an export of this module may be stored under.
 *
 * One per **grant**, not one per register. Two registers created under the same
 * read permission share a type, because the type exists to carry the grant into
 * the download step and two types carrying one grant are two copies of one fact
 * that can drift apart.
 *
 * `satisfies` binds the list to the enum, so a typo here is a compile error
 * rather than a row that falls to `OWNER_TYPE_DOWNLOAD_FALLBACK` at run time.
 */
export const LOGISTICS_EXPORT_OWNER_TYPES = [
    'logistics_export',
    'logistics_customs_export',
    'logistics_parcel_export',
    'logistics_delivery_proof_export',
    'logistics_claim_export',
    'logistics_authorisation_export',
];
export const LOGISTICS_EXPORT_GATES = {
    shipments: {
        permission: Permission.LOGISTICS_SHIPMENT_READ,
        owner_type: 'logistics_export',
    },
    cargo_items: {
        permission: Permission.LOGISTICS_SHIPMENT_READ,
        owner_type: 'logistics_export',
    },
    customs_records: {
        permission: Permission.LOGISTICS_CUSTOMS_READ,
        owner_type: 'logistics_customs_export',
    },
    clearance_events: {
        permission: Permission.LOGISTICS_CUSTOMS_READ,
        owner_type: 'logistics_customs_export',
    },
    authority_correspondence: {
        permission: Permission.LOGISTICS_CUSTOMS_READ,
        owner_type: 'logistics_customs_export',
    },
    parcels: {
        permission: Permission.LOGISTICS_PARCEL_READ,
        owner_type: 'logistics_parcel_export',
    },
    parcel_events: {
        permission: Permission.LOGISTICS_PARCEL_READ,
        owner_type: 'logistics_parcel_export',
    },
    delivery_proofs: {
        permission: Permission.LOGISTICS_DELIVERY_PROOF_READ,
        owner_type: 'logistics_delivery_proof_export',
    },
    claims: {
        permission: Permission.LOGISTICS_CLAIM_READ,
        owner_type: 'logistics_claim_export',
    },
    authorisations: {
        permission: Permission.LOGISTICS_AUTHORISATION_READ,
        owner_type: 'logistics_authorisation_export',
    },
};
export const LOGISTICS_EXPORT_FORMATS = ['csv', 'xlsx', 'pdf', 'docx'];
//# sourceMappingURL=logistics.js.map